import axios from 'axios';

// Get backend URL from environment variable
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect on login/auth endpoints - 401 is expected for wrong credentials
    const isAuthEndpoint = error.config?.url?.includes('/auth/login') || 
                          error.config?.url?.includes('/auth/staff-login') ||
                          error.config?.url?.includes('/auth/register');
    
    if (error.response?.status === 401 && !isAuthEndpoint) {
      // Token expired or invalid - redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API calls
export const authAPI = {
  register: async (userData) => {
    const response = await api.post('/api/auth/register', userData);
    return response.data;
  },
  
  login: async (credentials) => {
    const response = await api.post('/api/auth/login', credentials);
    return response.data;
  },
  
  staffLogin: async (credentials) => {
    const response = await api.post('/api/auth/staff-login', credentials);
    return response.data;
  },
  
  getCurrentUser: async () => {
    const response = await api.get('/api/auth/me');
    return response.data;
  },
};

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map(v => toCamelCase(v));
  } else if (obj !== null && obj.constructor === Object) {
    return Object.keys(obj).reduce((result, key) => {
      const camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
      result[camelKey] = toCamelCase(obj[key]);
      return result;
    }, {});
  }
  return obj;
};

// Colleges API calls
export const collegesAPI = {
  getColleges: async (params = {}) => {
    const response = await api.get('/api/colleges', { params });
    const data = response.data;
    // Transform snake_case to camelCase
    if (data.colleges) {
      data.colleges = toCamelCase(data.colleges);
    }
    return data;
  },
  
  getCollege: async (id) => {
    const response = await api.get(`/api/colleges/${id}`);
    return toCamelCase(response.data);
  },
  
  saveCollege: async (collegeId) => {
    const response = await api.post('/api/users/saved-colleges', {
      item_id: collegeId,
      item_type: 'college',
    });
    return response.data;
  },
  
  unsaveCollege: async (collegeId) => {
    const response = await api.delete(`/api/users/saved-colleges/${collegeId}`);
    return response.data;
  },
  
  getSavedColleges: async () => {
    const response = await api.get('/api/users/saved-colleges');
    return response.data;
  },
};

// Scholarships API calls
export const scholarshipsAPI = {
  getScholarships: async (params = {}) => {
    // NEW: Query funding_opportunities collection (scholarships)
    const response = await api.get('/api/funding/scholarships', { params });
    // LEGACY (kept for rollback): const response = await api.get('/api/scholarships', { params });
    const data = response.data;
    // Transform snake_case to camelCase
    if (data.scholarships) {
      data.scholarships = toCamelCase(data.scholarships);
    }
    return data;
  },
  
  getScholarship: async (id) => {
    // NEW: Query funding_opportunities collection
    const response = await api.get(`/api/funding/${id}`);
    // LEGACY (kept for rollback): const response = await api.get(`/api/scholarships/${id}`);
    return toCamelCase(response.data);
  },
  
  saveScholarship: async (scholarshipId) => {
    const response = await api.post('/api/users/saved-scholarships', {
      item_id: scholarshipId,
      item_type: 'scholarship',
    });
    return response.data;
  },
  
  unsaveScholarship: async (scholarshipId) => {
    const response = await api.delete(`/api/users/saved-scholarships/${scholarshipId}`);
    return response.data;
  },
  
  getSavedScholarships: async () => {
    const response = await api.get('/api/users/saved-scholarships');
    return response.data;
  },
};

// Funding Applied State API
export const fundingAppliedAPI = {
  getApplied: async (userId, sessionId) => {
    const params = {};
    if (userId) params.user_id = userId;
    else if (sessionId) params.session_id = sessionId;
    const response = await api.get('/api/funding/applied', { params });
    return response.data.applied || {};
  },
  
  checkApplied: async (fundingIds, userId, sessionId) => {
    const response = await api.post('/api/funding/applied/check', {
      funding_ids: fundingIds,
      user_id: userId,
      session_id: sessionId
    });
    return response.data.applied || {};
  },
  
  logIntent: async (fundingId, fundingType, userId, sessionId) => {
    const response = await api.post('/api/funding/outbound-intent', {
      funding_id: fundingId,
      funding_type: fundingType,
      user_id: userId,
      session_id: sessionId,
      action: 'apply_click',
      timestamp: new Date().toISOString()
    });
    return response.data;
  }
};

// Funding Saved Items API
export const fundingSavedAPI = {
  getSaved: async () => {
    const response = await api.get('/api/funding/saved');
    return response.data.saved || [];
  },
  
  save: async (fundingId) => {
    const response = await api.post('/api/funding/saved', { funding_id: fundingId });
    return response.data;
  },
  
  unsave: async (fundingId) => {
    const response = await api.delete(`/api/funding/saved/${fundingId}`);
    return response.data;
  }
};

// Grants API calls (NEW - from funding_opportunities collection)
export const grantsAPI = {
  getGrants: async (params = {}) => {
    // Fetch all grants for client-side filtering (match scholarships behavior)
    const response = await api.get('/api/funding/grants', { 
      params: { ...params, limit: 1000 } 
    });
    const data = response.data;
    if (data.grants) {
      data.grants = toCamelCase(data.grants);
    }
    return data;
  },
  
  getGrant: async (id) => {
    const response = await api.get(`/api/funding/${id}`);
    return toCamelCase(response.data);
  },
};

// Leads API calls
export const leadsAPI = {
  createLead: async (leadData) => {
    const response = await api.post('/api/leads', leadData);
    return response.data;
  },
};

// Profile API calls
export const profileAPI = {
  updateProfile: async (data) => {
    const response = await api.put('/api/user/profile', data);
    return response.data;
  },
  changePassword: async (data) => {
    const response = await api.post('/api/auth/change-password', data);
    return response.data;
  },
};

export default api;
